import type { MouseEventHandler } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button, { ButtonColorEnum, ButtonSizeEnum, ButtonVariantEnum } from 'components/stateless/Button/Button';
import Exporter, { EXCEL_EXPORT_MAX_ROW_COUNT, PDF_EXPORT_MAX_ROW_COUNT } from 'components/stateless/Exporter/Exporter';
import StatusDescriptionModal from 'components/stateless/Modal/common/status/StatusDescriptionModal';
import Pagination from 'components/stateless/Pagination/Pagination';
import SearchBorder from 'components/stateless/SearchForm/SearchBorder';
import SearchDatePicker from 'components/stateless/SearchForm/SearchDatePicker';
import SearchInput from 'components/stateless/SearchForm/SearchInput';
import SearchLabel from 'components/stateless/SearchForm/SearchLabel';
import SearchSelect from 'components/stateless/SearchForm/SearchSelect';
import TableBody from 'components/stateless/Table/TableBody';
import TableBorder from 'components/stateless/Table/TableBorder';
import type { HeaderType } from 'components/stateless/Table/TableHeader';
import TableHeader from 'components/stateless/Table/TableHeader';
import Td from 'components/stateless/Table/Td';
import TdLink from 'components/stateless/Table/TdLink';
import Tr from 'components/stateless/Table/Tr';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import { ROUTES_FI } from 'constants/routes/financier';
import getSelectOptions from 'constants/selectOptions';
import type { CURRENCY_TYPE, ENTERPRISE_TYPE } from 'enums';
import { SUCCESS_AR_STATUS } from 'enums';
import type { PageableType } from 'hooks/usePageable';
import type Pageable from 'models/Pageable';
import type { SuccessArVOModel } from 'models/vo/SuccessArVO';
import { getBlockedStatusClassName } from 'utils/classNames';
import getStatusTextClass from 'utils/classNames/getStatusTextClass';
import type { PDFExporterProps } from 'utils/exportFile/exportPDF.d';
import { requestFinancierSuccessArsList } from 'utils/http/api/financier/success-ars';
import { ModalSize, ModalType } from 'utils/modal/ModalWrapper';
import useModal from 'utils/modal/useModal';
import { getParsedSearchParams } from 'utils/searchParams';
import type { ColumnOption, ExportSpreadSheetProps } from 'utils/spreadSheet/types';
import { tableValueManage } from 'utils/valueManager/ValueManager';

import { FI_REGISTERED_AR_LIST_QS_KEY } from '../useFinancierARListController';

interface FinancierARListRegisteredTabProps {
  data: Pageable<SuccessArVOModel[]> | undefined;
  pageable: PageableType;
  paginate(page: number, sizePerPage: number): void;
  handleClickSearch: MouseEventHandler<HTMLButtonElement>;
}

function FinancierARListRegisteredTab({
  data,
  pageable,
  paginate,
  handleClickSearch,
}: FinancierARListRegisteredTabProps) {
  const { t } = useTranslation();
  const { register, reset, control } = useFormContext();
  const modal = useModal();

  const onClickRemoveFilter = (e: any) => {
    e.preventDefault();
    reset({});
  };

  const onClickSettlementStatusModal = (): void => {
    modal.show(
      <StatusDescriptionModal
        statusDescriptionEnum={Object.values(SUCCESS_AR_STATUS)}
        statusDescriptionEnumType="SUCCESS_AR_STATUS"
        statusDescriptionModalType="TEXT"
      />,
      {
        modalType: ModalType.ALERT,
        modalSize: ModalSize.XL,
      },
    );
  };

  const tableHeaders: HeaderType[] = [
    {
      headerText: t('text:Registration_Date'),
    },
    {
      headerText: t('text:Validated_By'),
    },
    {
      headerText: t('text:Anchor_Name'),
    },
    {
      headerText: t('text:Anchor_Tax_Code'),
    },
    {
      headerText: t('text:Partner_Name'),
    },
    {
      headerText: t('text:Partner_Tax_Code'),
    },
    {
      headerText: t('text:AR_Number'),
    },
    {
      headerText: t('text:AR_Issued_Date'),
    },
    {
      headerText: t('text:Original_Settlement_Date'),
    },
    {
      headerText: t('text:Modified_Settlement_Date'),
    },
    {
      headerText: t('text:AR_Amount'),
    },
    {
      headerText: t('text:Currency'),
    },
    {
      headerText: t('text:Financing_Eligibility'),
    },
    {
      headerText: t('text:Settlement_Status'),
      hasStatusDescription: true,
      showStatusDescriptionFunc: onClickSettlementStatusModal,
    },
    {
      headerText: '',
      colWidths: 50,
    },
  ];

  const renderResultTable = (data: SuccessArVOModel[] | undefined, isPdf: boolean = false) => {
    return data?.map((item, i) => {
      return (
        <Tr key={i} className={isPdf ? 'virtual-table-row' : ''}>
          <Td data={item.createdDateTime} format="datetime" />
          <Td data={t(`code:enterprise-type.${item.confirmedEnterpriseType}`)} />
          <Td data={item.anchorName} />
          <Td data={item.anchorTaxCode} />
          <Td data={item.anchorPartnerName} />
          <Td data={item.anchorPartnerTaxCode} />
          <Td data={item.arNumber} />
          <Td data={item.arIssuedDate} format="date" />
          <Td data={item.maturityDate} format="date" />
          <Td data={item.settlementDate} format="date" />
          <Td data={item.arAmount} format="number" />
          <Td data={item.currencyType} />
          <Td
            className={getBlockedStatusClassName(item.blockedByFinancier)}
            data={item.blockedByFinancier ? t('text:Ineligible') : t('text:Eligible')}
          />
          <Td
            className={getStatusTextClass('SUCCESS_AR_STATUS', item.successArStatus)}
            data={t(`code:success-ar-status.${item.successArStatus}`)}
            format="code"
          />
          {!isPdf && <TdLink path={ROUTES_FI.VIEW_TRANSACTION.AR_DETAIL_BUILD_PATH(item.successArId)} />}
        </Tr>
      );
    });
  };

  const onClickExportButton = async () => {
    const data = getParsedSearchParams(FI_REGISTERED_AR_LIST_QS_KEY).formSearchData;

    try {
      const successArListResponse = await requestFinancierSuccessArsList(0, EXCEL_EXPORT_MAX_ROW_COUNT, data);

      const successArListPdfResponse = successArListResponse.content.slice(0, PDF_EXPORT_MAX_ROW_COUNT);

      const jsonArrayData: unknown[] = successArListResponse.content.map(item => ({
        createdDateTime: tableValueManage(
          item.createdDateTime,
          t('format:datetime', { value: item.createdDateTime, key: 'datetime' }),
        ),
        anchorName: tableValueManage(item.anchorName),
        anchorTaxCode: tableValueManage(item.anchorTaxCode),
        anchorPartnerName: tableValueManage(item.anchorPartnerName),
        anchorPartnerTaxCode: tableValueManage(item.anchorPartnerTaxCode),
        arNumber: tableValueManage(item.arNumber),
        arIssuedDate: tableValueManage(item.arIssuedDate, t('format:date', { value: item.arIssuedDate, key: 'date' })),
        maturityDate: tableValueManage(item.maturityDate, t('format:date', { value: item.maturityDate, key: 'date' })),
        settlementDate: tableValueManage(
          item.settlementDate,
          t('format:date', { value: item.settlementDate, key: 'date' }),
        ),
        arAmount: Number(item.arAmount),
        currencyType: tableValueManage(item.currencyType),
        blockedByFinancier: item.blockedByFinancier ? t('text:Ineligible') : t('text:Eligible'),
        successArStatus: tableValueManage(item.successArStatus, t(`code:success-ar-status.${item.successArStatus}`)),
        confirmedEnterpriseType: t(`code:enterprise-type.${item.confirmedEnterpriseType}`),
      }));

      const excelColumns: ColumnOption<SuccessArVOModel>[] = [
        {
          header: t('text:Registration_Date'),
          key: 'createdDateTime',
        },
        {
          header: t('text:Validated_By'),
          key: 'confirmedEnterpriseType',
        },
        {
          header: t('text:Anchor_Name'),
          key: 'anchorName',
        },
        {
          header: t('text:Anchor_Tax_Code'),
          key: 'anchorTaxCode',
        },
        {
          header: t('text:Partner_Name'),
          key: 'anchorPartnerName',
        },
        {
          header: t('text:Partner_Tax_Code'),
          key: 'anchorPartnerTaxCode',
        },
        {
          header: t('text:AR_Number'),
          key: 'arNumber',
        },
        {
          header: t('text:AR_Issued_Date'),
          key: 'arIssuedDate',
        },
        {
          header: t('text:Original_Settlement_Date'),
          key: 'maturityDate',
        },
        {
          header: t('text:Modified_Settlement_Date'),
          key: 'settlementDate',
        },
        {
          header: t('text:AR_Amount'),
          key: 'arAmount',
        },
        {
          header: t('text:Currency'),
          key: 'currencyType',
        },
        {
          header: t('text:Financing_Eligibility'),
          key: 'blockedByFinancier',
        },
        {
          header: t('text:Settlement_Status'),
          key: 'successArStatus',
        },
      ];

      const excelExporterProps: ExportSpreadSheetProps<any> = {
        jsonArrayData,
        columns: excelColumns,
        options: {
          rowHeight: [{ position: 1, height: 30 }],
        },
      };

      const pdfExporterProps: PDFExporterProps = {
        tableHeaders: tableHeaders,
        tableBody: renderResultTable(successArListPdfResponse, true),
      };

      modal.show(<Exporter spreadSheetExporterProps={excelExporterProps} pdfExporterProps={pdfExporterProps} />, {
        title: t('text:Export_File'),
        closeBtnText: t('text:Close'),
      });
    } catch (e) {
      modal.show(e);
    }
  };

  return (
    <>
      <div className="content-area">
        <form>
          <SectionTitle title={t('text:Search')}>
            <Button
              variant={ButtonVariantEnum.OUTLINED}
              color={ButtonColorEnum.SECONDARY}
              onClick={onClickRemoveFilter}
            >
              {t('text:Remove_Filter')}
            </Button>
          </SectionTitle>
          <SearchBorder>
            <div className="row">
              <SearchLabel label={t('text:Registration_Date')} />
              <SearchDatePicker placeholder={t('text:from')} name="createdDateFrom" control={control} />
              <SearchDatePicker placeholder={t('text:to')} name="createdDateTo" control={control} />
              <SearchLabel label={t('text:Validated_By')} />
              <SearchSelect
                selectOptions={getSelectOptions<ENTERPRISE_TYPE>('ENTERPRISE_TYPE', 'ALL', true)}
                name="currentConfirmEnterpriseType"
                ref={register}
              />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Anchor_Name')} />
              <SearchInput name="anchorName" ref={register} />
              <SearchLabel label={t('text:Anchor_Tax_Code')} />
              <SearchInput name="anchorTaxCode" ref={register} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Partner_Name')} />
              <SearchInput name="partnerName" ref={register} />
              <SearchLabel label={t('text:Partner_Tax_Code')} />
              <SearchInput name="partnerTaxCode" ref={register} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:AR_Number')} />
              <SearchInput name="arNumber" ref={register} />
              <SearchLabel label={t('text:AR_Issued_Date')} />
              <SearchDatePicker placeholder={t('text:from')} name="arIssuedDateFrom" control={control} />
              <SearchDatePicker placeholder={t('text:to')} name="arIssuedDateTo" control={control} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Original_Settlement_Date')} />
              <SearchDatePicker placeholder={t('text:from')} name="maturityDateFrom" control={control} />
              <SearchDatePicker placeholder={t('text:to')} name="maturityDateTo" control={control} />
              <SearchLabel label={t('text:Modified_Settlement_Date')} />
              <SearchDatePicker placeholder={t('text:from')} name="settlementDateFrom" control={control} />
              <SearchDatePicker placeholder={t('text:to')} name="settlementDateTo" control={control} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:AR_Amount')} />
              <SearchInput col={2} placeholder={t('text:from')} name="minimumArAmount" ref={register} />
              <SearchInput col={2} placeholder={t('text:to')} name="maximumArAmount" ref={register} />
              <SearchLabel label={t('text:Currency')} />
              <SearchSelect
                selectOptions={getSelectOptions<CURRENCY_TYPE>('CURRENCY_TYPE', 'ALL', true)}
                name="currencyType"
                ref={register}
              />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Financing_Eligibility')} />
              <SearchSelect
                selectOptions={getSelectOptions<string>('FINANCING_ELIGIBILITY', 'ALL', true)}
                name="blocked"
                ref={register}
              />
              <SearchLabel
                label={t('text:Settlement_Status')}
                hasStatusDescription={true}
                showStatusDescriptionFunc={onClickSettlementStatusModal}
              />
              <SearchSelect
                selectOptions={getSelectOptions<SUCCESS_AR_STATUS>('SUCCESS_AR_STATUS', 'ALL', true)}
                name="successArStatus"
                ref={register}
              />
            </div>
          </SearchBorder>
          <div className="flex-center mt-4">
            <Button size={ButtonSizeEnum.LG} onClick={handleClickSearch}>
              {t('text:Search')}
            </Button>
          </div>
        </form>
      </div>
      <div className="division-border" />
      <div className="content-area">
        <SectionTitle title={t('text:Result')} />
        <div className="d-flex mb-2">
          <p className="total-data me-auto">
            {t('text:Total')} {data?.totalElements}
          </p>
          <Button onClick={onClickExportButton} variant={ButtonVariantEnum.OUTLINED}>
            {t('text:Export')}
          </Button>
        </div>
        <TableBorder>
          <TableHeader header={tableHeaders} />
          <TableBody numOfCol={tableHeaders.length}>{renderResultTable(data?.content)}</TableBody>
        </TableBorder>
        <Pagination pageable={pageable} paginate={paginate} />
      </div>
    </>
  );
}

export default FinancierARListRegisteredTab;
